import { IconButton } from "@controls";
import {
  BarsRegular,
  ChevronsLeftSolid,
  ChevronsRightSolid,
  Help,
} from "@icons";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadixNavigationMenu from "@radix-ui/react-navigation-menu";
import { DialogLiveRegions } from "@src/components/live-announcer";
import SkipLink from "@src/components/skip-link";
import { HELP_DOCS_URL } from "@src/config";
import classNames from "classnames";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { useAppContext } from "../../contexts/app-context/hook";
import { useLocalstorage } from "../../hooks/helperHooks";
import { MenuItem } from "../menu-item";

export interface GeneralLayoutProps {
  menuList: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
  extraMenu: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
}

function HelpMenuItem({
  minimized,
  setMobileMenuOpen,
}: {
  minimized: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <MenuItem
      href={HELP_DOCS_URL}
      icon={<Help size={16} />}
      label={t("help.label")}
      externalLabelSuffix={t("help.opensInNewTab")}
      minimized={minimized}
      setMobileMenuOpen={setMobileMenuOpen}
    />
  );
}

export function GeneralLayout({
  menuList,
  extraMenu = [],
}: GeneralLayoutProps) {
  const { t } = useTranslation();
  const { mobileMenuOpen, setMobileMenuOpen } = useAppContext();
  const [storedMinimizedValue, setStoredMinimizedValue] = useLocalstorage(
    "MENU-MINIMIZED",
    "false",
  );
  const minimized = storedMinimizedValue === "true";
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusToMenuButton = useRef(false);

  return (
    <>
      <SkipLink label={t("skipToContent")} targetId="main-content" />
      <div
        className={classNames(
          "w-full h-full lg:p-4",
          "flex flex-row bg-surface",
        )}
      >
        <div
          className={classNames(
            "hidden md:flex flex-col justify-between min-h-0 m-2 lg:m-0",
            {
              "w-14": minimized,
              "min-w-[260px] max-w-[260px]": !minimized,
            },
          )}
        >
          <div className="flex flex-col justify-between min-h-0 overflow-y-auto">
            <div className="rounded flex-grow w-full">
              <RadixNavigationMenu.Root aria-label={t("mainNavigation")}>
                <RadixNavigationMenu.List
                  className={classNames("flex flex-col", {
                    "items-center": minimized,
                  })}
                >
                  {menuList.map((menuItems) => (
                    <RadixNavigationMenu.Item key={menuItems.key}>
                      <menuItems.component
                        minimized={minimized}
                        setMobileMenuOpen={setMobileMenuOpen}
                      />
                    </RadixNavigationMenu.Item>
                  ))}
                </RadixNavigationMenu.List>
              </RadixNavigationMenu.Root>
            </div>
          </div>
          <div className="shrink-0">
            <RadixNavigationMenu.Root aria-label={t("secondaryNavigation")}>
              <RadixNavigationMenu.List
                className={classNames("flex flex-col", {
                  "items-center": minimized,
                })}
              >
                {extraMenu.map((menuItems) => (
                  <RadixNavigationMenu.Item
                    key={menuItems.key}
                    className="w-full"
                  >
                    <menuItems.component
                      minimized={minimized}
                      setMobileMenuOpen={setMobileMenuOpen}
                    />
                  </RadixNavigationMenu.Item>
                ))}
                <RadixNavigationMenu.Item>
                  <HelpMenuItem
                    minimized={minimized}
                    setMobileMenuOpen={setMobileMenuOpen}
                  />
                </RadixNavigationMenu.Item>
              </RadixNavigationMenu.List>
            </RadixNavigationMenu.Root>
            <div className="hidden md:flex w-full justify-end mt-2">
              <IconButton
                aria-label={minimized ? t("expandMenu") : t("collapseMenu")}
                size="medium"
                customIcon={
                  minimized ? <ChevronsRightSolid /> : <ChevronsLeftSolid />
                }
                onClick={() => setStoredMinimizedValue((!minimized).toString())}
              />
            </div>
          </div>
        </div>
        <main
          id="main-content"
          tabIndex={-1}
          className={classNames(
            "lg:ml-4 bg-page flex flex-grow flex-col h-full overflow-x-auto rounded-lg outline-none",
          )}
        >
          <div className="inline md:hidden">
            <Dialog.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-scrim bg-opacity-50" />
                <Dialog.Content
                  aria-label={t("navigationMenu")}
                  aria-describedby={undefined}
                  onEscapeKeyDown={() => {
                    returnFocusToMenuButton.current = true;
                  }}
                  onInteractOutside={() => {
                    returnFocusToMenuButton.current = true;
                  }}
                  onCloseAutoFocus={(event) => {
                    if (!returnFocusToMenuButton.current) return;
                    returnFocusToMenuButton.current = false;
                    event.preventDefault();
                    menuButtonRef.current?.focus();
                  }}
                  className={classNames(
                    "fixed top-0 h-full w-2/3 max-w-[350px] overflow-auto p-2 flex flex-col justify-between",
                    "bg-primary text-on-primary",
                    {
                      "-left-52": !mobileMenuOpen,
                      "left-0": mobileMenuOpen,
                    },
                  )}
                >
                  <div>
                    <RadixNavigationMenu.Root aria-label={t("mainNavigation")}>
                      <RadixNavigationMenu.List className={"flex flex-col"}>
                        {menuList.map((menuItems) => (
                          <RadixNavigationMenu.Item key={menuItems.key}>
                            <menuItems.component
                              minimized={false}
                              setMobileMenuOpen={setMobileMenuOpen}
                            />
                          </RadixNavigationMenu.Item>
                        ))}
                      </RadixNavigationMenu.List>
                    </RadixNavigationMenu.Root>
                  </div>
                  <div>
                    <RadixNavigationMenu.Root
                      aria-label={t("secondaryNavigation")}
                    >
                      <RadixNavigationMenu.List
                        className={classNames("flex flex-col", {
                          "items-center": minimized,
                        })}
                      >
                        {extraMenu.map((menuItems) => (
                          <RadixNavigationMenu.Item
                            key={menuItems.key}
                            className="w-full"
                          >
                            <menuItems.component
                              minimized={minimized}
                              setMobileMenuOpen={setMobileMenuOpen}
                            />
                          </RadixNavigationMenu.Item>
                        ))}
                        <RadixNavigationMenu.Item className="w-full">
                          <HelpMenuItem
                            minimized={false}
                            setMobileMenuOpen={setMobileMenuOpen}
                          />
                        </RadixNavigationMenu.Item>
                      </RadixNavigationMenu.List>
                    </RadixNavigationMenu.Root>
                  </div>
                  <DialogLiveRegions open={mobileMenuOpen} />
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
          <div
            className={classNames(
              "flex md:hidden shrink-0",
              "justify-between items-center w-full min-h-11 py-[7px] px-2 border-b bg-tint",
            )}
          >
            <button
              ref={menuButtonRef}
              aria-label={t("openMenu")}
              className="w-target h-target rounded text-primary"
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            >
              <BarsRegular />
            </button>
          </div>
          <div className="m-2 flex-auto overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}

export default GeneralLayout;
